import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import { MuiPickersUtilsProvider, DatePicker } from "material-ui-pickers";

import {
    Button, DialogContent, DialogActions, Typography, TextField,
    Tabs, Tab, Divider, CircularProgress, Chip, Avatar,
} from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";

import SelectField from "../../components/fields/SelectField";

import ModalContainer from "../../components/modal/ModalContainer";
import HubTimepicker from "../../components/timepicker/HubTimepicker";

const PINK = "#ff0c9c";
const PINK_BG = "rgba(255,29,121,0.07)";
const PINK_BORDER = "rgba(255,29,121,0.3)";

const MODE_EVENT = 0;
const MODE_VENUE = 1;

const VSUB_BROWSE = "browse";
const VSUB_DIRECT = "direct";

const STEP_FORM    = 0;
const STEP_VENUES  = 1;
const STEP_PREVIEW = 2;
const STEP_RESULT  = 3;

const STEP_TITLES = ["Sync from Oztix", "Select Venues", "Preview Events", "Import Complete"];

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const btnPrimary = { backgroundColor: PINK, color: "#fff", textTransform: "none", boxShadow: "none" };
const btnPrimaryDisabled = { opacity: 0.5 };

@inject("gigPromotionStore", "venueStore")
@observer
class OztixSyncModal extends Component {

    extractEventIdFromInput(input) {
        const trimmed = (input || "").trim();
        const match = trimmed.match(/\/event\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
        if (match) return match[1];
        return trimmed;
    }

    extractErrorMessage(err, fallback) {
        if (!err) return fallback;
        const body = (err.response && err.response.data) || err.data || err.response || {};
        if (body && typeof body === "object") {
            if (typeof body.errors === "string") return body.errors;
            if (typeof body.message === "string") return body.message;
            if (typeof body.error === "string") return body.error;
        }
        if (typeof body === "string" && body.length) return body;
        return err.message || fallback;
    }

    getInitialState() {
        return {
            mode: MODE_EVENT,
            step: STEP_FORM,
            eventLink: "",
            venueSubMode: VSUB_BROWSE,
            dateFrom: moment().startOf("month"),
            dateTo: moment().add(3, "months").endOf("month"),
            venueSearchState: "",
            venueSearchLocality: "",
            venueSearchName: "",
            directState: "",
            directLocality: "",
            directEventName: "",
            isDiscovering: false,
            discoveredVenues: [],
            selectedVenueIds: [],
            discoverError: null,
            isPreviewing: false,
            previewEvents: [],
            selectedEventIds: [],
            previewError: null,
            isImporting: false,
            result: null,
            importError: null,
            timeEndOverrides: {},
            venueOverrides: {},
            venueSuggestions: [],
        };
    }

    state = this.getInitialState();

    handleClose = () => {
        this.setState(this.getInitialState());
        this.props.onClose();
    };

    handleDiscoverVenues = () => {
        const { venueSearchState, venueSearchLocality, venueSearchName } = this.state;
        const params = {};
        if (venueSearchState.trim())    params.state    = venueSearchState.trim();
        if (venueSearchLocality.trim()) params.locality = venueSearchLocality.trim();
        if (venueSearchName.trim())     params.name     = venueSearchName.trim();

        this.setState({ isDiscovering: true, discoverError: null });
        this.props.gigPromotionStore.discoverOztixVenues(params).then(venues => {
            this.setState({
                isDiscovering: false,
                discoveredVenues: venues,
                selectedVenueIds: [],
                step: STEP_VENUES,
            });
        }).catch(err => {
            this.setState({
                isDiscovering: false,
                discoverError: this.extractErrorMessage(err, "Failed to load venues. Please try again."),
            });
        });
    };

    handlePreviewEvents = () => {
        const { mode, eventLink, venueSubMode, directState, directLocality, directEventName, selectedVenueIds, dateFrom, dateTo } = this.state;

        let params;
        if (mode === MODE_EVENT) {
            if (!eventLink.trim()) return;
            params = { eventId: this.extractEventIdFromInput(eventLink) };
        } else if (venueSubMode === VSUB_DIRECT) {
            params = {
                dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
                dateTo: moment(dateTo).format("YYYY-MM-DD"),
            };
            if (directState.trim())     params.state     = directState.trim();
            if (directLocality.trim())  params.locality  = directLocality.trim();
            if (directEventName.trim()) params.eventName = directEventName.trim();
        } else {
            if (selectedVenueIds.length === 0) return;
            params = {
                venueIds: selectedVenueIds,
                dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
                dateTo: moment(dateTo).format("YYYY-MM-DD"),
            };
        }

        this.setState({ isPreviewing: true, previewError: null });
        Promise.all([
            this.props.gigPromotionStore.previewOztixEvents(params),
            this.props.venueStore.getUserBookableVenues(),
        ]).then(([events, venues]) => {
            const venueSuggestions = (venues || []).map(v => ({ value: v.id, label: v.fullName }));

            // Pre-populate overrides for already-synced events from stored HotGiggity data
            const timeEndOverrides = {};
            const venueOverrides = {};
            (events || []).forEach(ev => {
                if (ev.alreadySynced) {
                    if (ev.syncedTimeEnd != null) {
                        timeEndOverrides[ev.oztixEventId] = moment(ev.syncedTimeEnd, "HH:mm:ss");
                    }
                    if (ev.syncedVenueId != null) {
                        venueOverrides[ev.oztixEventId] = ev.syncedVenueId;
                    }
                }
            });

            this.setState({
                isPreviewing: false,
                previewEvents: events,
                selectedEventIds: [],
                venueSuggestions,
                timeEndOverrides,
                venueOverrides,
                step: STEP_PREVIEW,
            });
        }).catch(err => {
            this.setState({
                isPreviewing: false,
                previewError: this.extractErrorMessage(err, "Failed to fetch events. Please try again."),
            });
        });
    };

    handleImport = () => {
        const { selectedEventIds, previewEvents, timeEndOverrides, venueOverrides } = this.state;
        if (selectedEventIds.length === 0) return;

        const events = selectedEventIds.map(id => {
            const ev = previewEvents.find(e => e.oztixEventId === id);
            const override = timeEndOverrides[id];
            const timeEnd = override !== undefined
                ? (override ? moment(override).format("HH:mm:ss") : null)
                : (ev ? ev.timeEnd : null);
            const venueId = venueOverrides[id];
            return {
                oztixEventId: id,
                timeEnd,
                ...(venueId ? { venueId } : {}),
            };
        });

        this.setState({ isImporting: true, importError: null });
        this.props.gigPromotionStore.syncFromOztix({ oztixEventIds: selectedEventIds, events }).then(response => {
            const result = response && response.response ? response.response : response;
            this.setState({ isImporting: false, result, step: STEP_RESULT });
            if (this.props.onSyncComplete) this.props.onSyncComplete();
        }).catch(err => {
            this.setState({ isImporting: false, importError: this.extractErrorMessage(err, "Import failed. Please try again.") });
        });
    };

    toggleVenue = venueId => {
        this.setState(prev => {
            const ids = prev.selectedVenueIds;
            return ids.includes(venueId)
                ? { selectedVenueIds: ids.filter(id => id !== venueId) }
                : { selectedVenueIds: [...ids, venueId] };
        });
    };

    toggleAllVenues = () => {
        const { discoveredVenues, selectedVenueIds } = this.state;
        const allIds = discoveredVenues.map(v => v.venueId);
        this.setState({ selectedVenueIds: selectedVenueIds.length === allIds.length ? [] : allIds });
    };

    toggleEvent = oztixEventId => {
        this.setState(prev => {
            const ids = prev.selectedEventIds;
            return ids.includes(oztixEventId)
                ? { selectedEventIds: ids.filter(id => id !== oztixEventId) }
                : { selectedEventIds: [...ids, oztixEventId] };
        });
    };

    toggleAllEvents = () => {
        const { previewEvents, selectedEventIds } = this.state;
        const allIds = previewEvents.map(e => e.oztixEventId);
        this.setState({ selectedEventIds: selectedEventIds.length === allIds.length ? [] : allIds });
    };

    renderError(msg) {
        if (!msg) return null;
        return (
            <div style={{ marginTop: 14, padding: "10px 14px", background: "#ffebee", borderRadius: 6, border: "1px solid #ffcdd2" }}>
                <Typography variant="body2" style={{ color: "#c62828" }}>{msg}</Typography>
            </div>
        );
    }

    renderScrollList({ count, label, allSelected, onToggleAll, children, maxHeight, paddingBottom }) {
        return (
            <Fragment>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        {count} {label}{count !== 1 ? "s" : ""} found
                    </Typography>
                    <Button size="small" onClick={onToggleAll} style={{ textTransform: "none", fontSize: 12, color: PINK }}>
                        {allSelected ? "Deselect all" : "Select all"}
                    </Button>
                </div>
                {count === 0 ? (
                    <Typography variant="body2" style={{ color: "rgba(0,0,0,0.45)", fontStyle: "italic", textAlign: "center", padding: "24px 0" }}>
                        No results found.
                    </Typography>
                ) : (
                    <div style={{ maxHeight: maxHeight || 340, overflowY: "auto", paddingRight: 4, marginRight: -4, paddingBottom: paddingBottom || 0 }}>
                        {children}
                    </div>
                )}
            </Fragment>
        );
    }

    renderDateRange() {
        const { dateFrom, dateTo } = this.state;
        return (
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <div style={{ display: "flex", gap: 12 }}>
                    <DatePicker label="Date from" value={dateFrom} onChange={val => this.setState({ dateFrom: val })} format="DD/MM/YYYY" autoOk style={{ flex: 1 }} />
                    <DatePicker label="Date to" value={dateTo} onChange={val => this.setState({ dateTo: val })} format="DD/MM/YYYY" autoOk minDate={dateFrom} style={{ flex: 1 }} />
                </div>
            </MuiPickersUtilsProvider>
        );
    }

    renderSubModeToggle() {
        const { venueSubMode } = this.state;
        const active   = { background: PINK, color: "#fff", borderColor: PINK, fontWeight: 600 };
        const inactive = { background: "transparent", color: PINK, borderColor: PINK };
        return (
            <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
                <button
                    onClick={() => this.setState({ venueSubMode: VSUB_BROWSE, discoverError: null, previewError: null })}
                    style={{ flex: 1, padding: "7px 12px", border: `1px solid ${PINK}`, borderRadius: "4px 0 0 4px", cursor: "pointer", fontSize: 13, transition: "all 0.15s", ...(venueSubMode === VSUB_BROWSE ? active : inactive) }}
                >
                    Browse venues first
                </button>
                <button
                    onClick={() => this.setState({ venueSubMode: VSUB_DIRECT, discoverError: null, previewError: null })}
                    style={{ flex: 1, padding: "7px 12px", border: `1px solid ${PINK}`, borderLeft: "none", borderRadius: "0 4px 4px 0", cursor: "pointer", fontSize: 13, transition: "all 0.15s", ...(venueSubMode === VSUB_DIRECT ? active : inactive) }}
                >
                    Get events directly
                </button>
            </div>
        );
    }

    renderStepForm() {
        const { mode, eventLink, venueSubMode, venueSearchState, venueSearchLocality, venueSearchName, directState, directLocality, directEventName, isDiscovering, discoverError, isPreviewing, previewError } = this.state;

        return (
            <Fragment>
                <Tabs
                    value={mode}
                    onChange={(_, v) => this.setState({ mode: v, discoverError: null, previewError: null, eventLink: "", venueSearchState: "", venueSearchLocality: "", venueSearchName: "", directState: "", directLocality: "", directEventName: "" })}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ marginBottom: 4 }}
                >
                    <Tab label="Single Event" />
                    <Tab label="By Venue & Date" />
                </Tabs>
                <Divider style={{ marginBottom: 16 }} />

                {mode === MODE_EVENT ? (
                    <div>
                        <Typography variant="body2" style={{ marginBottom: 8, color: "rgba(0,0,0,0.55)" }}>
                            Paste the Oztix event link to preview and import a single event.
                        </Typography>
                        <TextField
                            label="Oztix Event Link"
                            value={eventLink}
                            onChange={e => this.setState({ eventLink: e.target.value })}
                            fullWidth variant="outlined"
                            placeholder="https://tickets.oztix.com.au/outlet/event/..."
                            inputProps={{ style: { fontSize: 13 } }}
                        />
                        {eventLink.trim() && (
                            <Typography variant="caption" style={{ marginTop: 6, display: "block", color: "rgba(0,0,0,0.45)" }}>
                                Event ID: <strong style={{ color: "rgba(0,0,0,0.7)" }}>{this.extractEventIdFromInput(eventLink)}</strong>
                            </Typography>
                        )}
                        {this.renderError(previewError)}
                    </div>
                ) : (
                    <div>
                        {this.renderSubModeToggle()}

                        {venueSubMode === VSUB_BROWSE ? (
                            <Fragment>
                                <Typography variant="body2" style={{ marginBottom: 12, color: "rgba(0,0,0,0.55)" }}>
                                    Search Oztix for venues, pick the ones you want, then fetch their upcoming events.
                                </Typography>
                                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                    <TextField
                                        label="State"
                                        value={venueSearchState}
                                        onChange={e => this.setState({ venueSearchState: e.target.value })}
                                        variant="outlined"
                                        placeholder="e.g. QLD"
                                        style={{ flex: 1 }}
                                        inputProps={{ style: { fontSize: 14, textTransform: "uppercase" } }}
                                    />
                                    <TextField
                                        label="Suburb / Locality"
                                        value={venueSearchLocality}
                                        onChange={e => this.setState({ venueSearchLocality: e.target.value })}
                                        variant="outlined"
                                        placeholder="e.g. Fitzroy"
                                        style={{ flex: 2 }}
                                        inputProps={{ style: { fontSize: 14 } }}
                                    />
                                </div>
                                <TextField
                                    label="Venue name"
                                    value={venueSearchName}
                                    onChange={e => this.setState({ venueSearchName: e.target.value })}
                                    fullWidth variant="outlined"
                                    placeholder="e.g. Tivoli, Metro Theatre"
                                    inputProps={{ style: { fontSize: 14 } }}
                                    onKeyPress={e => { if (e.key === "Enter") this.handleDiscoverVenues(); }}
                                    style={{ marginBottom: 4 }}
                                />
                                <Typography variant="caption" style={{ color: "rgba(0,0,0,0.4)", display: "block", marginBottom: 12 }}>
                                    All filters are optional — leave blank to browse all Oztix venues.
                                </Typography>
                                {this.renderDateRange()}
                                {this.renderError(discoverError)}
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Typography variant="body2" style={{ marginBottom: 12, color: "rgba(0,0,0,0.55)" }}>
                                    Search Oztix for events directly within the date range — no venue selection required.
                                </Typography>
                                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                    <TextField
                                        label="State"
                                        value={directState}
                                        onChange={e => this.setState({ directState: e.target.value })}
                                        variant="outlined"
                                        placeholder="e.g. QLD"
                                        style={{ flex: 1 }}
                                        inputProps={{ style: { fontSize: 14, textTransform: "uppercase" } }}
                                    />
                                    <TextField
                                        label="Suburb / Locality"
                                        value={directLocality}
                                        onChange={e => this.setState({ directLocality: e.target.value })}
                                        variant="outlined"
                                        placeholder="e.g. Fitzroy"
                                        style={{ flex: 2 }}
                                        inputProps={{ style: { fontSize: 14 } }}
                                    />
                                </div>
                                <TextField
                                    label="Event name"
                                    value={directEventName}
                                    onChange={e => this.setState({ directEventName: e.target.value })}
                                    fullWidth variant="outlined"
                                    placeholder="e.g. Blues Festival, Live at the Metro"
                                    inputProps={{ style: { fontSize: 14 } }}
                                    onKeyPress={e => { if (e.key === "Enter") this.handlePreviewEvents(); }}
                                    style={{ marginBottom: 4 }}
                                />
                                <Typography variant="caption" style={{ color: "rgba(0,0,0,0.4)", display: "block", marginBottom: 12 }}>
                                    All filters are optional — leave blank to browse all Oztix events.
                                </Typography>
                                {this.renderDateRange()}
                                {this.renderError(previewError)}
                            </Fragment>
                        )}
                    </div>
                )}
            </Fragment>
        );
    }

    renderStepVenues() {
        const { discoveredVenues, selectedVenueIds, previewError } = this.state;
        const allSelected = selectedVenueIds.length === discoveredVenues.length && discoveredVenues.length > 0;

        return (
            <Fragment>
                {this.renderScrollList({
                    count: discoveredVenues.length,
                    label: "venue",
                    allSelected,
                    onToggleAll: this.toggleAllVenues,
                    children: discoveredVenues.map(v => {
                        const checked = selectedVenueIds.includes(v.venueId);
                        const addr = [v.address, v.locality, v.state, v.postcode].filter(Boolean).join(", ");
                        return (
                            <div
                                key={v.venueId}
                                onClick={() => this.toggleVenue(v.venueId)}
                                style={{
                                    display: "flex", alignItems: "flex-start", gap: 10,
                                    padding: "10px 8px", borderRadius: 6, marginBottom: 6,
                                    cursor: "pointer", userSelect: "none",
                                    background: checked ? PINK_BG : "rgba(0,0,0,0.02)",
                                    border: `1px solid ${checked ? PINK_BORDER : "rgba(0,0,0,0.08)"}`,
                                    transition: "background 0.15s, border-color 0.15s",
                                }}
                            >
                                {checked
                                    ? <CheckBoxIcon style={{ color: PINK, flexShrink: 0, marginTop: 2 }} />
                                    : <CheckBoxOutlineBlankIcon style={{ color: "rgba(0,0,0,0.3)", flexShrink: 0, marginTop: 2 }} />
                                }
                                <div style={{ flex: 1 }}>
                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                                        {v.name || `Venue ${v.venueId}`}
                                    </Typography>
                                    {addr && <Typography variant="caption" style={{ color: "rgba(0,0,0,0.5)", display: "block" }}>{addr}</Typography>}
                                    <Typography variant="caption" style={{ color: "rgba(0,0,0,0.35)" }}>ID: {v.venueId}</Typography>
                                </div>
                            </div>
                        );
                    }),
                })}
                {this.renderError(previewError)}
            </Fragment>
        );
    }

    renderStepPreview() {
        const { previewEvents, selectedEventIds, importError, timeEndOverrides, venueOverrides, venueSuggestions } = this.state;
        const allSelected = selectedEventIds.length === previewEvents.length && previewEvents.length > 0;

        return (
            <Fragment>
                {this.renderScrollList({
                    count: previewEvents.length,
                    label: "event",
                    allSelected,
                    onToggleAll: this.toggleAllEvents,
                    maxHeight: 480,
                    paddingBottom: 100,
                    children: previewEvents.map(ev => {
                        const checked = selectedEventIds.includes(ev.oztixEventId);
                        const dateStr = ev.dateStart ? moment(ev.dateStart).format("ddd D MMM YYYY") : null;
                        const timeStr = ev.timeStart ? ev.timeStart.substring(0, 5) : null;
                        return (
                            <div
                                key={ev.oztixEventId}
                                onClick={() => this.toggleEvent(ev.oztixEventId)}
                                style={{
                                    display: "flex", alignItems: "flex-start", gap: 10,
                                    padding: "10px 8px 14px", borderRadius: 6, marginBottom: 6,
                                    cursor: "pointer", userSelect: "none",
                                    background: checked ? PINK_BG : "rgba(0,0,0,0.02)",
                                    border: `1px solid ${checked ? PINK_BORDER : "rgba(0,0,0,0.08)"}`,
                                    transition: "background 0.15s, border-color 0.15s",
                                }}
                            >
                                {checked
                                    ? <CheckBoxIcon style={{ color: PINK, flexShrink: 0 }} />
                                    : <CheckBoxOutlineBlankIcon style={{ color: "rgba(0,0,0,0.3)", flexShrink: 0 }} />
                                }
                                {ev.eventImageUrl ? (
                                    <Avatar src={ev.eventImageUrl} variant="rounded" style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 4 }} />
                                ) : (
                                    <Avatar variant="rounded" style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 4, background: "#e0e0e0", fontSize: 14, color: "#888" }}>
                                        {(ev.title || "?").charAt(0)}
                                    </Avatar>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                        <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                                            {ev.title || ev.actName || "Untitled"}
                                        </Typography>
                                        <Chip
                                            label={ev.alreadySynced ? "Will Update" : "New"}
                                            style={{ height: 18, fontSize: 10, fontWeight: 700, pointerEvents: "none", backgroundColor: ev.alreadySynced ? "#fff3e0" : "#e8f5e9", color: ev.alreadySynced ? "#e65100" : "#2e7d32" }}
                                        />
                                    </div>
                                    <Typography variant="caption" style={{ color: "rgba(0,0,0,0.5)", display: "block", lineHeight: 1.4 }}>
                                        {[dateStr, timeStr ? `Start: ${timeStr}` : null].filter(Boolean).join(" · ")}{ev.venueName ? ` · ${ev.venueName}` : ""}
                                        {ev.venueState ? ` (${ev.venueState})` : ""}
                                    </Typography>
                                    {ev.actGenres && (
                                        <Typography variant="caption" style={{ color: "rgba(0,0,0,0.4)", display: "block" }}>
                                            {ev.actGenres}
                                        </Typography>
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                                        <div
                                            style={{ display: "inline-block", width: 160 }}
                                            onClick={e => { e.persist(); e.stopPropagation(); }}
                                        >
                                            <HubTimepicker
                                                label="End time"
                                                value={timeEndOverrides[ev.oztixEventId] !== undefined
                                                    ? timeEndOverrides[ev.oztixEventId]
                                                    : (ev.timeEnd ? moment(ev.timeEnd, "HH:mm:ss") : null)}
                                                onChange={val => this.setState(prev => ({
                                                    timeEndOverrides: { ...prev.timeEndOverrides, [ev.oztixEventId]: val }
                                                }))}
                                            />
                                        </div>
                                        <div
                                            style={{ display: "inline-block", minWidth: 200 }}
                                            onClick={e => { e.persist(); e.stopPropagation(); }}
                                        >
                                            <SelectField
                                                id={`venue-select-${ev.oztixEventId}`}
                                                label="Venue"
                                                suggestions={venueSuggestions}
                                                onChange={val => this.setState(prev => ({
                                                    venueOverrides: { ...prev.venueOverrides, [ev.oztixEventId]: val }
                                                }))}
                                                value={venueOverrides[ev.oztixEventId] || null}
                                                placeholder="Select Venue"
                                            />
                                        </div>
                                        {ev.ticketPrice != null && (
                                            <Typography variant="caption" style={{ color: "rgba(0,0,0,0.4)" }}>
                                                ${Number(ev.ticketPrice).toFixed(2)}
                                            </Typography>
                                        )}
                                        {ev.alreadySynced && ev.gigPromotionId && (
                                            <a
                                                href={`/promotion/${ev.gigPromotionId}/edit`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: PINK, fontSize: 11, textDecoration: "none", flexShrink: 0 }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                View existing →
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }),
                })}
                {this.renderError(importError)}
            </Fragment>
        );
    }

    renderStepResult() {
        const { result } = this.state;
        if (!result) return null;

        // Response shape: { total, created: [{ gigPromotionId, oztixEventId, updated }] }
        const allItems    = Array.isArray(result.created) ? result.created : [];
        const newItems     = allItems.filter(i => !i.updated);
        const updatedItems = allItems.filter(i => i.updated);

        return (
            <Fragment>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    <Chip label={`${newItems.length} created`}  style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />
                    <Chip label={`${updatedItems.length} updated`} style={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 600 }} />
                </div>
                <Typography variant="body2" style={{ color: "rgba(0,0,0,0.55)", marginBottom: allItems.length ? 12 : 0 }}>
                    Imported events are saved as drafts and can be reviewed and published through the normal gig promotion workflow.
                </Typography>
                {allItems.length > 0 && (
                    <div>
                        {allItems.map((item, i) => (
                            <div key={item.oztixEventId || i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                <Chip
                                    label={item.updated ? "Updated" : "Created"}
                                    style={{ height: 18, fontSize: 10, fontWeight: 700, pointerEvents: "none", backgroundColor: item.updated ? "#fff3e0" : "#e8f5e9", color: item.updated ? "#e65100" : "#2e7d32" }}
                                />
                                <Typography variant="caption" style={{ color: "rgba(0,0,0,0.45)", flex: 1, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.oztixEventId}
                                </Typography>
                                {item.gigPromotionId && (
                                    <a href={`/gig/promotion/${item.gigPromotionId}/edit`} target="_blank" rel="noopener noreferrer" style={{ color: PINK, fontSize: 12, flexShrink: 0, textDecoration: "none" }}>
                                        View →
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {allItems.length === 0 && (
                    <Typography variant="body2" style={{ color: "rgba(0,0,0,0.45)", fontStyle: "italic" }}>No changes were made.</Typography>
                )}
            </Fragment>
        );
    }

    renderActions() {
        const { step, mode, venueSubMode, eventLink, directState, selectedVenueIds, selectedEventIds, isDiscovering, isPreviewing, isImporting } = this.state;

        if (step === STEP_FORM) {
            if (mode === MODE_EVENT) {
                const canGo = eventLink.trim().length > 0 && !isPreviewing;
                return (
                    <Fragment>
                        <Button onClick={this.handleClose} disabled={isPreviewing} style={{ textTransform: "none" }}>Cancel</Button>
                        <Button variant="contained" onClick={this.handlePreviewEvents} disabled={!canGo} style={{ ...btnPrimary, ...(canGo ? {} : btnPrimaryDisabled) }}>
                            {isPreviewing ? <Fragment><CircularProgress size={16} style={{ marginRight: 8, color: "#fff" }} />Loading…</Fragment> : "Preview Event"}
                        </Button>
                    </Fragment>
                );
            }

            if (venueSubMode === VSUB_BROWSE) {
                return (
                    <Fragment>
                        <Button onClick={this.handleClose} disabled={isDiscovering} style={{ textTransform: "none" }}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleDiscoverVenues} disabled={isDiscovering} startIcon={!isDiscovering && <SearchIcon />} style={btnPrimary}>
                            {isDiscovering ? <Fragment><CircularProgress size={16} style={{ marginRight: 8, color: "#fff" }} />Searching…</Fragment> : "Find Venues"}
                        </Button>
                    </Fragment>
                );
            }

            const canDirect = !isPreviewing;
            return (
                <Fragment>
                    <Button onClick={this.handleClose} disabled={isPreviewing} style={{ textTransform: "none" }}>Cancel</Button>
                    <Button variant="contained" onClick={this.handlePreviewEvents} disabled={!canDirect} style={{ ...btnPrimary, ...(canDirect ? {} : btnPrimaryDisabled) }}>
                        {isPreviewing ? <Fragment><CircularProgress size={16} style={{ marginRight: 8, color: "#fff" }} />Loading…</Fragment> : "Preview Events"}
                    </Button>
                </Fragment>
            );
        }

        if (step === STEP_VENUES) {
            return (
                <Fragment>
                    <Button onClick={() => this.setState({ step: STEP_FORM, previewError: null })} disabled={isPreviewing} startIcon={<ArrowBackIcon />} style={{ marginRight: "auto", textTransform: "none" }}>
                        Back
                    </Button>
                    <Button onClick={this.handleClose} disabled={isPreviewing} style={{ textTransform: "none" }}>Cancel</Button>
                    <Button variant="contained" onClick={this.handlePreviewEvents} disabled={selectedVenueIds.length === 0 || isPreviewing} style={btnPrimary}>
                        {isPreviewing
                            ? <Fragment><CircularProgress size={16} style={{ marginRight: 8, color: "#fff" }} />Loading…</Fragment>
                            : `Preview Events${selectedVenueIds.length > 0 ? ` (${selectedVenueIds.length} venue${selectedVenueIds.length !== 1 ? "s" : ""})` : ""}`}
                    </Button>
                </Fragment>
            );
        }

        if (step === STEP_PREVIEW) {
            const backTo = (mode === MODE_VENUE && venueSubMode === VSUB_BROWSE) ? STEP_VENUES : STEP_FORM;
            return (
                <Fragment>
                    <Button onClick={() => this.setState({ step: backTo, importError: null })} disabled={isImporting} startIcon={<ArrowBackIcon />} style={{ marginRight: "auto", textTransform: "none" }}>
                        Back
                    </Button>
                    <Button onClick={this.handleClose} disabled={isImporting} style={{ textTransform: "none" }}>Cancel</Button>
                    <Button variant="contained" onClick={this.handleImport} disabled={selectedEventIds.length === 0 || isImporting} style={btnPrimary}>
                        {isImporting
                            ? <Fragment><CircularProgress size={16} style={{ marginRight: 8, color: "#fff" }} />Importing…</Fragment>
                            : `Import ${selectedEventIds.length > 0 ? selectedEventIds.length + " " : ""}Selected`}
                    </Button>
                </Fragment>
            );
        }

        return (
            <Button variant="contained" onClick={this.handleClose} style={btnPrimary}>Done</Button>
        );
    }

    render() {
        const { open } = this.props;
        const { step } = this.state;

        return (
            <ModalContainer open={open} title={STEP_TITLES[step]} onClose={this.handleClose} maxWidth="sm">
                <DialogContent>
                    {step === STEP_FORM    && this.renderStepForm()}
                    {step === STEP_VENUES  && this.renderStepVenues()}
                    {step === STEP_PREVIEW && this.renderStepPreview()}
                    {step === STEP_RESULT  && this.renderStepResult()}
                </DialogContent>
                <DialogActions>
                    {this.renderActions()}
                </DialogActions>
            </ModalContainer>
        );
    }
}

export default OztixSyncModal;
